import { z } from 'zod';
import { ApiError } from '../../api/client';

export const categoryFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(120, 'At most 120 characters'),
  parentId: z.string(),
});

/** A catalog category also picks its accounts ('' = from the parent, then the system account). */
export const productCategoryFormSchema = categoryFormSchema.extend({
  incomeAccountId: z.string(),
  cogsAccountId: z.string(),
  valuationAccountId: z.string(),
});
export type CategoryFormValues = z.infer<typeof productCategoryFormSchema>;

/** Category account fields, the role their account needs and the system account they fall back to. */
export const CATEGORY_ACCOUNT_FIELDS = [
  { field: 'incomeAccountId', role: 'SALES', label: 'Sales income' },
  { field: 'cogsAccountId', role: 'COGS', label: 'Cost of goods sold' },
  { field: 'valuationAccountId', role: 'INVENTORY', label: 'Stock valuation' },
] as const;

export const posCategoryFormSchema = categoryFormSchema.extend({
  sortOrder: z
    .string()
    .trim()
    .refine((v) => /^\d{1,4}$/.test(v), 'A whole number, 0 to 9999'),
});
export type PosCategoryFormValues = z.infer<typeof posCategoryFormSchema>;

export const brandFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(120, 'At most 120 characters'),
  isActive: z.boolean(),
});
export type BrandFormValues = z.infer<typeof brandFormSchema>;

export const unitFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(60, 'At most 60 characters'),
  symbol: z.string().trim().min(1, 'Symbol is required').max(20, 'At most 20 characters'),
  allowsFraction: z.boolean(),
  isActive: z.boolean(),
});
export type UnitFormValues = z.infer<typeof unitFormSchema>;

/** "24x24, 12x24\n16x16" → ["24x24", "12x24", "16x16"] (trimmed, blanks and repeats dropped). */
export function optionsFromText(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(/[\n,]/)) {
    const v = raw.trim();
    if (v && !seen.has(v.toLowerCase())) {
      seen.add(v.toLowerCase());
      out.push(v);
    }
  }
  return out;
}

export const attributeFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(80, 'At most 80 characters'),
    dataType: z.enum(['TEXT', 'NUMBER', 'OPTION']),
    unitHint: z.string().trim().max(20, 'At most 20 characters'),
    options: z.string(),
    isActive: z.boolean(),
  })
  .superRefine((v, ctx) => {
    const options = optionsFromText(v.options);
    if (options.some((o) => o.length > 80))
      ctx.addIssue({
        code: 'custom',
        path: ['options'],
        message: 'Each option at most 80 characters',
      });
    if (options.length > 200)
      ctx.addIssue({ code: 'custom', path: ['options'], message: 'At most 200 options' });
  });
export type AttributeFormValues = z.infer<typeof attributeFormSchema>;

/**
 * Puts API errors on form fields: validation details ("body.name") and the "already used" codes
 * (any *_TAKEN / *_EXISTS / DUPLICATE* code lands on `name`). Returns true when something landed.
 */
export function applyFieldErrors(
  error: unknown,
  fields: readonly string[],
  setError: (field: string, e: { type: string; message: string }) => void,
): boolean {
  if (!(error instanceof ApiError)) return false;
  let mapped = false;
  for (const d of error.details) {
    const field = d.path.replace(/^body\./, '').split('.')[0] ?? '';
    if (fields.includes(field)) {
      setError(field, { type: 'server', message: d.message });
      mapped = true;
    }
  }
  if (
    !mapped &&
    fields.includes('name') &&
    /TAKEN|EXISTS|DUPLICATE|CONFLICT_NAME/.test(error.code)
  ) {
    setError('name', { type: 'server', message: error.message });
    mapped = true;
  }
  return mapped;
}
